<hlm-dialog [state]="state()" (closed)="close()">
  <hlm-dialog-content
    *brnDialogContent="let ctx"
    class="w-[min(48rem,calc(100vw-2rem))] md:w-[min(48rem,calc(100vw-2rem))] max-w-none min-w-0 h-[750px] max-h-[90vh] flex flex-col"
  >
    <app-wizard-dialog-header
      eyebrow="Planungsvorlage"
      [title]="computedTitle()"
      [steps]="wizardSteps()"
      (stepJump)="jumpToStep($any($event.id))"
    />

    <!-- Step Content -->
    <div hlmSeparator class="my-2"></div>
    <div class="flex-1 py-4 relative w-full min-h-0">
      <div
        class="h-full w-full transition-all duration-300 ease-in-out overflow-hidden"
      >
        <!-- Step 1: Basic Info -->
        @if (currentStep() === 1) {
          <div
            class="h-full overflow-y-auto animate-in fade-in-50 duration-300 px-1"
          >
            <app-step-lead [text]="stepLead()" />
            <form [formGroup]="basicInfoForm" class="flex flex-col gap-5">

              <!-- Template Name -->
              <app-suffix-input
                label="Vorlagenname"
                inputType="text"
                formControlName="name"
                required
                [errorText]="
                  basicInfoForm.get('name')?.invalid &&
                  basicInfoForm.get('name')?.touched
                    ? 'Name ist erforderlich (min. 3 Zeichen)'
                    : null
                "
              />

              <!-- Template Type Selection -->
              <app-select-field
                label="Vorlagentyp"
                placeholder="Typ auswählen"
                [options]="templateTypeOptions"
                required
                [value]="templateType()"
                (selected)="setTemplateType($any($event))"
                helpText="Miete ergänzt den Schritt Miet-Informationen."
              />

              <!-- Description -->
              <app-textarea-field
                label="Beschreibung"
                formControlName="description"
              />
            </form>
          </div>
        }

        <!-- Step 2: Equipment Selection -->
        @if (
          (isLeaseTemplate() && currentStep() === 2) ||
          (!isLeaseTemplate() && currentStep() === 2)
        ) {
          <div
            class="h-full overflow-y-auto animate-in fade-in-50 duration-300 px-1"
          >
            <app-step-lead [text]="stepLead()" />
            <div class="flex flex-col gap-5">
              <app-field-shell label="Solarmodul" required>
                <app-module-selector
                  [modules]="equipmentStore.allModules()"
                  [isLoading]="equipmentStore.isLoading()"
                  [selectedModuleId]="selectedModuleId()"
                  (moduleSelected)="onModuleSelected($event)"
                />
              </app-field-shell>

              <app-field-shell
                label="Wechselrichter"
                required
                [hint]="selectedInverterIds().length + ' / 5 ausgewählt'"
                tooltipText="Wählen Sie bis zu fünf Wechselrichter. Für jede Anlage wird der passende Wechselrichter ausgewählt."
              >
                <app-inverter-selector
                  [inverters]="equipmentStore.allInverters()"
                  [isLoading]="equipmentStore.isLoading()"
                  [selectedInverterIds]="selectedInverterIds()"
                  (invertersSelected)="onInvertersSelected($event)"
                />
              </app-field-shell>

              <app-field-shell
                label="Speicher"
                [hint]="selectedStorageIds().length + ' / 2 ausgewählt'"
                tooltipText="Optional: Wählen Sie bis zu zwei Speicher. Die Varianten werden für die Anlage verglichen."
              >
                <app-storage-selector
                  [storages]="equipmentStore.allStorages()"
                  [isLoading]="equipmentStore.isLoading()"
                  [selectedStorageIds]="selectedStorageIds()"
                  (storagesSelected)="onStoragesSelected($event)"
                />
              </app-field-shell>
            </div>
          </div>
        }

        <!-- Step 3: Lease Info (only if lease selected) -->
        @if (isLeaseTemplate() && currentStep() === 3) {
          <div
            class="h-full overflow-y-auto animate-in fade-in-50 duration-300 px-1"
          >
            <app-step-lead [text]="stepLead()" />
            <form [formGroup]="leaseForm" class="flex flex-col gap-5">

              <!-- Contract Duration PV -->
              <app-suffix-input
                label="Vertragslaufzeit PV-Anlage"
                tooltipText="Wie viele Jahre die PV-Anlage vermietet wird."
                suffix="Jahre"
                formControlName="contractDurationPv"
                required
                [errorText]="
                  leaseForm.get('contractDurationPv')?.invalid &&
                  leaseForm.get('contractDurationPv')?.touched
                    ? 'Wert erforderlich (mindestens 1 Jahr)'
                    : null
                "
              />

              <!-- Contract Duration Storage (only show if storage selected) -->
              @if (selectedStorageIds().length > 0) {
                <app-suffix-input
                  label="Vertragslaufzeit Speicher"
                  tooltipText="Wie viele Jahre der Speicher vermietet wird. Kann von der Laufzeit der PV-Anlage abweichen."
                  suffix="Jahre"
                  formControlName="contractDurationStorage"
                  required
                />
              }

              <!-- Foreign Capital Quota -->
              <app-suffix-input
                label="Quote Fremdkapital"
                tooltipText="Anteil der Investition, der mit Fremdkapital statt Eigenkapital finanziert wird."
                suffix="%"
                formControlName="foreignCapitalQuota"
                required
                [errorText]="
                  leaseForm.get('foreignCapitalQuota')?.invalid &&
                  leaseForm.get('foreignCapitalQuota')?.touched
                    ? 'Wert zwischen 0 und 100 erforderlich'
                    : null
                "
              />

              <!-- Insurance Costs Per Year -->
              <app-suffix-input
                label="Versicherungskosten p.a."
                tooltipText="Geschätzte Versicherungskosten für die gesamte Anlage pro Jahr in Euro."
                suffix="€"
                formControlName="insuranceCostsPerYear"
                required
                [errorText]="
                  leaseForm.get('insuranceCostsPerYear')?.invalid &&
                  leaseForm.get('insuranceCostsPerYear')?.touched
                    ? 'Wert erforderlich'
                    : null
                "
              />

              <!-- Maintenance Costs Per Year (%) -->
              <app-suffix-input
                label="Wartungskosten p.a."
                tooltipText="Jährliche Wartungskosten als Prozentsatz des PV-Anschaffungswerts ohne Speicher."
                suffix="%"
                formControlName="maintenanceCostsPercentage"
                required
                [errorText]="
                  leaseForm.get('maintenanceCostsPercentage')?.invalid &&
                  leaseForm.get('maintenanceCostsPercentage')?.touched
                    ? 'Wert erforderlich'
                    : null
                "
              />

              <!-- Target Profit (%) -->
              <app-suffix-input
                label="Zielgewinn"
                tooltipText="Angestrebter Gewinnanteil pro Anlage in Prozent. Die Berechnungsgrundlage sollte vor einer Mietpreiskalkulation festgelegt werden."
                suffix="%"
                formControlName="zielgewinn"
                required
                [errorText]="
                  leaseForm.get('zielgewinn')?.invalid &&
                  leaseForm.get('zielgewinn')?.touched
                    ? 'Wert erforderlich'
                    : null
                "
              />
            </form>
          </div>
        }

        <!-- Step 4: Installation Costs -->
        @if (
          (isLeaseTemplate() && currentStep() === 4) ||
          (!isLeaseTemplate() && currentStep() === 3)
        ) {
          <div
            class="h-full overflow-y-auto animate-in fade-in-50 duration-300 px-1"
          >
            <app-step-lead [text]="stepLead()" />
            <form [formGroup]="costsForm" class="flex flex-col gap-5">

              <div class="grid grid-cols-2 gap-4">
                <!-- DC Electrical Material -->
                <app-suffix-input
                  label="DC Elektromaterial"
                  tooltipText="Kosten für Gleichstromkabel, Stecker und weiteres DC-Material pro Anlage."
                  suffix="€"
                  formControlName="dcElectricalMaterialCosts"
                  required
                />

                <!-- AC Installation -->
                <app-suffix-input
                  label="AC Montagekosten"
                  tooltipText="Kosten für die Installation auf der Wechselstromseite pro Anlage."
                  suffix="€"
                  formControlName="acInstallationCosts"
                  required
                />

                <!-- Scaffolding -->
                <app-suffix-input
                  label="Gerüstkosten"
                  tooltipText="Kosten für das benötigte Gerüst pro Anlage."
                  suffix="€"
                  formControlName="scaffoldingCosts"
                  required
                />

                <!-- Additional Roof Surcharge -->
                <app-suffix-input
                  label="Dachaufschlag"
                  tooltipText="Pauschaler Aufschlag je belegter Dachfläche, wenn mehr als eine Dachfläche genutzt wird. Tragen Sie 0 ein, wenn kein Aufschlag anfällt."
                  suffix="€/Dachfläche"
                  formControlName="additionalRoofSurchargePerArea"
                  required
                />

                <!-- Grid Connection -->
                <app-suffix-input
                  label="Netzanschlusskosten"
                  tooltipText="Kosten für den Netzanschluss der Anlage."
                  suffix="€"
                  formControlName="gridConnectionCosts"
                  required
                />

                <!-- Other Costs -->
                <app-suffix-input
                  label="Sonstige Kosten"
                  tooltipText="Weitere einmalige Anlagenkosten, die in den anderen Kostenfeldern nicht enthalten sind."
                  suffix="€"
                  formControlName="otherCosts"
                />
              </div>

              <div class="pt-2 space-y-4 border-t border-border">
                <div class="flex items-center gap-2">
                  <ng-icon
                    name="lucideSettings"
                    class="w-5 h-5 text-primary"
                  ></ng-icon>
                  <h5 class="font-normal text-sm text-foreground">
                    Weitere Annahmen
                  </h5>
                </div>

                <div class="grid grid-cols-2 gap-4">
                  <app-suffix-input
                    label="Einspeisetarif bis 10 kWp"
                    tooltipText="Vergütung pro eingespeister Kilowattstunde für Anlagen bis 10 kWp. Als Eurobetrag eingeben, z. B. 0,077 für 7,7 ct."
                    suffix="€/kWh"
                    formControlName="feedInTariffUpTo10kwp"
                  />

                  <app-suffix-input
                    label="Einspeisetarif bis 40 kWp"
                    tooltipText="Vergütung pro eingespeister Kilowattstunde für Anlagen über 10 bis 40 kWp. Als Eurobetrag eingeben, z. B. 0,0666 für 6,66 ct."
                    suffix="€/kWh"
                    formControlName="feedInTariffUpTo40kwp"
                  />

                  @if (!isLeaseTemplate()) {
                    <app-suffix-input
                      label="Wartungskosten p.a."
                      tooltipText="Jährliche Wartungskosten als Prozentsatz des PV-Anschaffungswerts ohne Speicher."
                      suffix="%"
                      formControlName="maintenanceCostsPerYear"
                    />
                  }

                  <div class="flex items-center gap-3">
                    <hlm-switch formControlName="wallbox" />
                    <span class="text-sm text-foreground">Wallbox vorgesehen</span>
                  </div>
                </div>
              </div>
            </form>
          </div>
        }

        <!-- Step 5: Analysis Values -->
        @if (
          (isLeaseTemplate() && currentStep() === 5) ||
          (!isLeaseTemplate() && currentStep() === 4)
        ) {
          <div
            class="h-full overflow-y-auto animate-in fade-in-50 duration-300 px-1"
          >
            <app-step-lead [text]="stepLead()" />
            <form [formGroup]="analysisValuesForm" class="flex flex-col gap-5">

              <div class="grid grid-cols-2 gap-4">
                <!-- Electricity Price -->
                <app-suffix-input
                  label="Standard-Strompreis"
                  [decimals]="4"
                  tooltipText="Wird nur verwendet, wenn der Analyse noch kein Strompreis vorliegt. Beispiel: 0,325 €/kWh entspricht 32,5 ct/kWh."
                  suffix="€/kWh"
                  formControlName="electricityPrice"
                  required
                  [errorText]="
                    analysisValuesForm.get('electricityPrice')?.invalid &&
                    analysisValuesForm.get('electricityPrice')?.touched
                      ? 'Strompreis ist erforderlich'
                      : null
                  "
                />

                <!-- Electricity Price Increase -->
                <app-suffix-input
                  label="Strompreissteigerung"
                  tooltipText="Angenommene jährliche Veränderung des Strompreises. 2 bedeutet 2 % pro Jahr."
                  suffix="%/Jahr"
                  formControlName="electricityPriceIncrease"
                  required
                  [errorText]="
                    analysisValuesForm.get('electricityPriceIncrease')
                      ?.invalid &&
                    analysisValuesForm.get('electricityPriceIncrease')
                      ?.touched
                      ? 'Strompreissteigerung ist erforderlich'
                      : null
                  "
                />

                <!-- Calculation Period -->
                <app-suffix-input
                  label="Berechnungszeitraum"
                  tooltipText="Zeitraum, über den Erträge und Wirtschaftlichkeit der Anlage berechnet werden, in Jahren."
                  suffix="Jahre"
                  formControlName="calculationPeriod"
                  required
                  [errorText]="
                    analysisValuesForm.get('calculationPeriod')?.invalid &&
                    analysisValuesForm.get('calculationPeriod')?.touched
                      ? 'Berechnungszeitraum ist erforderlich'
                      : null
                  "
                />
              </div>
            </form>
          </div>
        }

        <!-- Step 6: Summary -->
        @if (
          (isLeaseTemplate() && currentStep() === 6) ||
          (!isLeaseTemplate() && currentStep() === 5)
        ) {
          <div
            class="h-full overflow-y-auto animate-in fade-in-50 duration-300 px-1"
          >
            <app-step-lead [text]="stepLead()" />
            <div class="space-y-4">
              <!-- Summary Sections -->
              <div class="flex flex-col gap-6">
                <!-- Basic Info Summary -->
                <app-summary-section title="Grunddaten" (edit)="jumpToStep(1)">
                  <app-summary-row
                    label="Name"
                    [value]="basicInfoForm.get('name')?.value || '—'"
                  />
                  <app-summary-row
                    label="Typ"
                    [value]="
                      basicInfoForm.get('templateType')?.value === 'lease'
                        ? 'Miete'
                        : 'Kauf'
                    "
                  />
                  @if (basicInfoForm.get("description")?.value) {
                    <app-summary-row
                      label="Beschreibung"
                      [value]="basicInfoForm.get('description')?.value"
                    />
                  }
                </app-summary-section>

                <!-- Equipment Summary -->
                <app-summary-section title="Komponenten" (edit)="jumpToStep(2)">
                  @if (selectedModule()) {
                    <app-summary-row
                      label="Solarmodul"
                      [value]="selectedModule()!.name"
                    />
                  }
                  @if (selectedInverterIds().length > 0) {
                    <app-summary-row
                      label="Wechselrichter"
                      [value]="selectedInverterIds().length + ''"
                      mono
                    />
                  }
                  @if (selectedStorageIds().length > 0) {
                    <app-summary-row
                      label="Speicher"
                      [value]="selectedStorageIds().length + ''"
                      mono
                    />
                  }
                </app-summary-section>

                <!-- Lease Info Summary (only for lease templates) -->
                @if (isLeaseTemplate()) {
                  <app-summary-section
                    title="Leasing-Informationen"
                    (edit)="jumpToStep(3)"
                  >
                    <app-summary-row
                      label="Vertragslaufzeit PV"
                      [value]="
                        (leaseForm.get('contractDurationPv')?.value || '—') +
                        ' Jahre'
                      "
                      mono
                    />
                    @if (selectedStorageIds().length > 0) {
                      <app-summary-row
                        label="Vertragslaufzeit Speicher"
                        [value]="
                          (leaseForm.get('contractDurationStorage')?.value ||
                            '—') + ' Jahre'
                        "
                        mono
                      />
                    }
                    <app-summary-row
                      label="Fremdkapitalquote"
                      [value]="
                        (leaseForm.get('foreignCapitalQuota')?.value || '—') +
                        '%'
                      "
                      mono
                    />
                    <app-summary-row
                      label="Versicherungskosten p.a."
                      [value]="
                        (leaseForm.get('insuranceCostsPerYear')?.value ||
                          '—') + ' €'
                      "
                      mono
                    />
                    <app-summary-row
                      label="Wartungskosten p.a."
                      [value]="
                        (leaseForm.get('maintenanceCostsPercentage')?.value ||
                          '—') + '%'
                      "
                      mono
                    />
                    <app-summary-row
                      label="Zielgewinn"
                      [value]="
                        (leaseForm.get('zielgewinn')?.value || '—') + '%'
                      "
                      mono
                    />
                  </app-summary-section>
                }

                <!-- Costs Summary -->
                <app-summary-section
                  title="Installationskosten"
                  (edit)="jumpToStep(isLeaseTemplate() ? 4 : 3)"
                >
                  @if (costsForm.get("dcElectricalMaterialCosts")?.value) {
                    <app-summary-row
                      label="DC Elektromaterial"
                      [value]="
                        costsForm.get('dcElectricalMaterialCosts')?.value +
                        ' €'
                      "
                      mono
                    />
                  }
                  @if (costsForm.get("acInstallationCosts")?.value) {
                    <app-summary-row
                      label="AC Montagekosten"
                      [value]="
                        costsForm.get('acInstallationCosts')?.value + ' €'
                      "
                      mono
                    />
                  }
                  @if (costsForm.get("scaffoldingCosts")?.value) {
                    <app-summary-row
                      label="Gerüstkosten"
                      [value]="costsForm.get('scaffoldingCosts')?.value + ' €'"
                      mono
                    />
                  }
                  @if (costsForm.get("additionalRoofSurchargePerArea")?.value) {
                    <app-summary-row
                      label="Dachaufschlag"
                      [value]="
                        costsForm.get('additionalRoofSurchargePerArea')
                          ?.value + ' €/Dachfläche'
                      "
                      mono
                    />
                  }
                  @if (costsForm.get("gridConnectionCosts")?.value) {
                    <app-summary-row
                      label="Netzanschlusskosten"
                      [value]="
                        costsForm.get('gridConnectionCosts')?.value + ' €'
                      "
                      mono
                    />
                  }
                  @if (costsForm.get("otherCosts")?.value) {
                    <app-summary-row
                      label="Sonstige Kosten"
                      [value]="costsForm.get('otherCosts')?.value + ' €'"
                      mono
                    />
                  }
                  <app-summary-row
                    label="Einspeisetarif bis 10 kWp"
                    [value]="
                      (costsForm.get('feedInTariffUpTo10kwp')?.value || 0) +
                      ' €/kWh'
                    "
                    mono
                  />
                  <app-summary-row
                    label="Einspeisetarif bis 40 kWp"
                    [value]="
                      (costsForm.get('feedInTariffUpTo40kwp')?.value || 0) +
                      ' €/kWh'
                    "
                    mono
                  />
                  @if (!isLeaseTemplate()) {
                    <app-summary-row
                      label="Wartungskosten p.a."
                      [value]="
                        (costsForm.get('maintenanceCostsPerYear')?.value ||
                          0) + '%'
                      "
                      mono
                    />
                  }
                  <app-summary-row
                    label="Wallbox"
                    [value]="costsForm.get('wallbox')?.value ? 'Ja' : 'Nein'"
                  />
                </app-summary-section>

                <!-- Analysis Values Summary -->
                <app-summary-section
                  title="Analysewerte"
                  (edit)="jumpToStep(isLeaseTemplate() ? 5 : 4)"
                >
                  <app-summary-row
                    label="Strompreis"
                    [value]="
                      (analysisValuesForm.get('electricityPrice')?.value ||
                        '—') + ' €/kWh'
                    "
                    mono
                  />
                  <app-summary-row
                    label="Preissteigerung p.a."
                    [value]="
                      (analysisValuesForm.get('electricityPriceIncrease')
                        ?.value || '—') + '%'
                    "
                    mono
                  />
                  <app-summary-row
                    label="Berechnungszeitraum"
                    [value]="
                      (analysisValuesForm.get('calculationPeriod')?.value ||
                        '—') + ' Jahre'
                    "
                    mono
                  />
                </app-summary-section>
              </div>

            </div>
          </div>
        }

        <!-- Error Message -->
        @if (error()) {
          <div
            class="absolute bottom-0 left-1 right-1 bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg"
          >
            <p class="text-sm">{{ error() }}</p>
          </div>
        }
      </div>
    </div>

    <!-- Dialog Footer with Step Navigation -->
    <div hlmSeparator></div>
    <app-wizard-dialog-footer
      [currentStep]="currentStep()"
      [totalSteps]="wizardSteps().length"
      [isFirstStep]="currentStep() === 1"
      [isLastStep]="currentStep() === wizardSteps().length"
      [canProceed]="canProceedFromCurrentStep()"
      [isSubmitting]="isSubmitting()"
      [submitLabel]="computedSubmitButtonText()"
      (back)="prevStep()"
      (next)="nextStep()"
      (submit)="onSubmit(ctx)"
      (cancel)="close()"
    />
  </hlm-dialog-content>
</hlm-dialog>
